import React from 'react';
import {
    Box,
    Typography,
    Container,
    Paper,
    Divider,
} from '@mui/material';
import { useAppTheme } from '../App';

const TermsOfService: React.FC = () => {
    const { getThemeStyles } = useAppTheme();
    const theme = getThemeStyles();

    return (
        <Box sx={{ 
            background: theme.background, 
            minHeight: '100vh',
            py: 4
        }}>
            <Container maxWidth="md">
                <Paper sx={{ 
                    background: theme.cardBackground,
                    border: `1px solid ${theme.borderColor}`,
                    borderRadius: 3,
                    p: { xs: 3, sm: 4 },
                    boxShadow: `0 4px 12px ${theme.shadowColor}`,
                }}>
                    <Typography 
                        variant="h3" 
                        sx={{ 
                            color: theme.textPrimary,
                            fontWeight: 700,
                            mb: 1,
                            textAlign: 'center'
                        }}
                    >
                        Terms of Service
                    </Typography>
                    
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: theme.textSecondary,
                            textAlign: 'center',
                            mb: 4
                        }}
                    >
                        Last updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>

                    <Divider sx={{ borderColor: theme.borderColor, mb: 4 }} />

                    <Box sx={{ color: theme.textPrimary, '& h4': { color: theme.textPrimary, fontWeight: 600, mt: 3, mb: 2 } }}>
                        
                        <Typography variant="h4">1. Acceptance of Terms</Typography>
                        <Typography paragraph>
                            By accessing and using TheVisaAI (&quot;the Service&quot;), you accept and agree to be bound by the terms 
                            and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                        </Typography>

                        <Typography variant="h4">2. Service Description</Typography>
                        <Typography paragraph>
                            TheVisaAI provides AI-powered guidance and analysis for UK visa applications, including:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Global Talent Visa profile analysis and scoring</li>
                            <li>Personal statement template generation</li>
                            <li>Document review and recommendations</li>
                            <li>AI-powered chat assistance for visa guidance</li>
                            <li>Tourist visa guidance and information</li>
                        </Box>

                        <Typography variant="h4">3. Service Provider</Typography>
                        <Typography paragraph>
                            <strong>Service Provider:</strong> TheVisaAI<br />
                            <strong>Location:</strong> United Kingdom<br />
                            <strong>Contact Email:</strong> thevisaai.team@gmail.com<br />
                            <strong>Business Type:</strong> Individual/Sole Trader
                        </Typography>

                        <Typography variant="h4">4. User Account and Registration</Typography>
                        <Typography paragraph>
                            To access our services, you must:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Provide accurate and complete registration information</li>
                            <li>Maintain the security of your account credentials</li>
                            <li>Accept responsibility for all activities under your account</li>
                            <li>Notify us immediately of any unauthorized use</li>
                        </Box>

                        <Typography variant="h4">5. Subscription Services and Billing</Typography>
                        <Typography paragraph>
                            We offer both free and paid subscription services:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>Free Tier:</strong> Limited access to basic features</li>
                            <li><strong>Pro Subscription:</strong> Full access to all premium features</li>
                            <li><strong>Billing:</strong> Processed securely through RevenueCat and Paddle</li>
                            <li><strong>Renewals:</strong> Subscriptions renew automatically unless cancelled</li>
                            <li><strong>Cancellation:</strong> You may cancel your subscription at any time</li>
                        </Box>

                        <Typography variant="h4">6. Refund Policy</Typography>
                        <Typography paragraph>
                            Refunds may be available in accordance with our refund policy:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Requests must be made within 7 days of purchase</li>
                            <li>Refunds are processed through our payment provider (Paddle)</li>
                            <li>Service usage may affect refund eligibility</li>
                            <li>Contact us at thevisaai.team@gmail.com for refund requests</li>
                        </Box>

                        <Typography variant="h4">7. Acceptable Use</Typography>
                        <Typography paragraph>
                            You agree to use our service only for lawful purposes and in accordance with these Terms. You agree NOT to:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li>Use the service for any illegal or unauthorized purpose</li>
                            <li>Upload false, misleading, or fraudulent documents</li>
                            <li>Attempt to circumvent security measures or access restrictions</li>
                            <li>Abuse, harass, or send spam through our platform</li>
                            <li>Reverse engineer or attempt to extract our AI models</li>
                        </Box>

                        <Typography variant="h4">8. Content and Intellectual Property</Typography>
                        <Typography paragraph>
                            Content ownership and intellectual property rights:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>Your Content:</strong> You retain ownership of documents and information you upload</li>
                            <li><strong>Our Service:</strong> TheVisaAI retains all rights to the platform, AI models, and generated analysis</li>
                            <li><strong>Generated Content:</strong> AI-generated analysis and templates are provided for your use</li>
                            <li><strong>License:</strong> You grant us a limited license to process your content for service delivery</li>
                        </Box>

                        <Typography variant="h4">9. Disclaimer and Limitations</Typography>
                        <Typography paragraph>
                            <strong>IMPORTANT:</strong> TheVisaAI provides guidance and analysis tools, but:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>Not Legal Advice:</strong> Our service does not constitute legal advice</li>
                            <li><strong>No Guarantee:</strong> We cannot guarantee visa application success</li>
                            <li><strong>Professional Consultation:</strong> Always consult with qualified immigration lawyers for legal advice</li>
                            <li><strong>Official Sources:</strong> Check gov.uk and official sources for the latest requirements</li>
                            <li><strong>AI Limitations:</strong> AI analysis may not be 100% accurate</li>
                        </Box>

                        <Typography variant="h4">10. Limitation of Liability</Typography>
                        <Typography paragraph>
                            To the maximum extent permitted by law, TheVisaAI shall not be liable for any indirect, 
                            incidental, special, consequential, or punitive damages, including without limitation, loss of 
                            profits, data, use, goodwill, or other intangible losses.
                        </Typography>

                        <Typography variant="h4">11. Service Availability</Typography>
                        <Typography paragraph>
                            We strive to maintain service availability but cannot guarantee uninterrupted access. We reserve 
                            the right to modify, suspend, or discontinue the service with reasonable notice.
                        </Typography>

                        <Typography variant="h4">12. Data Protection</Typography>
                        <Typography paragraph>
                            Your personal data is protected in accordance with our Privacy Policy and UK data protection laws. 
                            We implement appropriate technical and organizational measures to ensure data security.
                        </Typography>

                        <Typography variant="h4">13. Termination</Typography>
                        <Typography paragraph>
                            Either party may terminate this agreement at any time:
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                            <li><strong>By You:</strong> Delete your account or stop using the service</li>
                            <li><strong>By Us:</strong> For violation of terms, with reasonable notice</li>
                            <li><strong>Data Deletion:</strong> Account data will be deleted upon termination</li>
                        </Box>

                        <Typography variant="h4">14. Governing Law</Typography>
                        <Typography paragraph>
                            These Terms shall be interpreted and governed in accordance with the laws of England and Wales. 
                            Any disputes shall be subject to the exclusive jurisdiction of the English courts.
                        </Typography>

                        <Typography variant="h4">15. Changes to Terms</Typography>
                        <Typography paragraph>
                            We reserve the right to modify these terms at any time. We will notify users of material changes 
                            via email or through the service. Continued use constitutes acceptance of modified terms.
                        </Typography>

                        <Typography variant="h4">16. Contact Information</Typography>
                        <Typography paragraph>
                            For questions about these Terms of Service, please contact us:
                        </Typography>
                        <Typography paragraph sx={{ pl: 2 }}>
                            <strong>Email:</strong> thevisaai.team@gmail.com<br />
                            <strong>Subject Line:</strong> Terms of Service Inquiry
                        </Typography>

                        <Divider sx={{ borderColor: theme.borderColor, my: 3 }} />
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default TermsOfService;
